import { useEffect, useMemo, useState } from "react";
import {
  Avatar, Badge, Box, Card, Chip, Dialog, Pagination, Stack, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, Typography, Alert,
} from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { packagesApi } from "../api/resources";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../i18n/LanguageContext";
import { COLORS } from "../theme";

const PAGE_SIZE = 8;

const courierLabel = (p) => (p.courier === "Otro" && p.courierNote ? `Otro (${p.courierNote})` : p.courier);

const startOfWeek = () => {
  const d = new Date();
  const day = d.getDay();
  const diff = (day === 0 ? -6 : 1) - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
};
const startOfMonth = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1);
};

export default function HistorialPage() {
  const { isAdmin } = useAuth();
  const { t, lang } = useLanguage();
  const [delivered, setDelivered] = useState([]);
  const [preset, setPreset] = useState("semana");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [page, setPage] = useState(1);
  const [lightbox, setLightbox] = useState(null);

  const fmtDate = (iso) => new Date(iso).toLocaleDateString(lang === "en" ? "en-US" : "es-MX", { day: "2-digit", month: "short", year: "numeric" });

  const range = useMemo(() => {
    if (preset === "semana") return { from: startOfWeek(), to: null };
    if (preset === "mes") return { from: startOfMonth(), to: null };
    if (preset === "todo") return { from: null, to: null };
    return { from: from ? new Date(from) : null, to: to ? new Date(to + "T23:59:59") : null };
  }, [preset, from, to]);

  useEffect(() => {
    const params = { status: "entregado" };
    if (range.from) params.from = range.from.toISOString();
    if (range.to) params.to = range.to.toISOString();
    packagesApi.list(params).then(setDelivered);
    setPage(1);
  }, [range]);

  const totalPages = Math.max(1, Math.ceil(delivered.length / PAGE_SIZE));
  const pageItems = delivered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const chip = (key, label) => (
    <Chip
      label={label}
      onClick={() => setPreset(key)}
      sx={{
        cursor: "pointer",
        bgcolor: preset === key ? COLORS.ink : COLORS.paperCard,
        color: preset === key ? COLORS.brassSoft : COLORS.inkSoft,
        border: `1px solid ${COLORS.line}`,
      }}
    />
  );

  const openLightbox = (src, label) => setLightbox({ src, label });

  return (
    <Box>
      <Typography variant="h5">{t("historial.title")}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t("historial.subtitleAdmin")}
      </Typography>
      {!isAdmin && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t("historial.readOnlyNote")}
        </Alert>
      )}

      <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }} useFlexGap>
        {chip("semana", t("historial.thisWeek"))}
        {chip("mes", t("historial.thisMonth"))}
        {chip("todo", t("historial.all"))}
        <Chip
          icon={<CalendarMonthIcon sx={{ fontSize: 15 }} />}
          label={t("historial.range")}
          onClick={() => setPreset("custom")}
          sx={{
            cursor: "pointer",
            bgcolor: preset === "custom" ? COLORS.ink : COLORS.paperCard,
            color: preset === "custom" ? COLORS.brassSoft : COLORS.inkSoft,
            border: `1px solid ${COLORS.line}`,
          }}
        />
        {preset === "custom" && (
          <>
            <TextField size="small" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            <Typography sx={{ alignSelf: "center" }} variant="caption">
              {t("historial.to")}
            </Typography>
            <TextField size="small" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </>
        )}
      </Stack>

      <TableContainer component={Card}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: "#F4F5EF" }}>
              <TableCell>{t("historial.colDept")}</TableCell>
              <TableCell>{t("historial.colPackageObs")}</TableCell>
              <TableCell>{t("historial.colReceivedDelivered")}</TableCell>
              <TableCell>{t("historial.colDate")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pageItems.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 5, color: "text.secondary" }}>
                  {t("historial.noResultsRange")}
                </TableCell>
              </TableRow>
            )}
            {pageItems.map((p) => {
              const photos = p.photos || [];
              const evidencePhotos = p.deliveryPhotos || [];
              return (
                <TableRow key={p._id} hover>
                  <TableCell>
                    <Chip label={p.unitId} size="small" sx={{ fontWeight: 600 }} />
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1.5}>
                      {photos[0] ? (
                        <Badge badgeContent={photos.length > 1 ? photos.length : 0} color="primary">
                          <Avatar
                            variant="rounded"
                            src={photos[0]}
                            sx={{ width: 48, height: 48, cursor: "pointer" }}
                            onClick={() => openLightbox(photos[0], t("common.photo"))}
                          />
                        </Badge>
                      ) : (
                        <Box
                          sx={{
                            width: 48, height: 48, borderRadius: 1, border: `1px dashed ${COLORS.line}`,
                            display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9,
                            color: "text.secondary", textAlign: "center", flexShrink: 0,
                          }}
                        >
                          {t("historial.noFoto")}
                        </Box>
                      )}
                      <Box>
                        <Typography variant="body2">
                          {courierLabel(p)} — {p.desc || t("common.photo")}
                        </Typography>
                        {p.tracking && (
                          <Typography variant="caption" color="text.secondary">
                            {p.tracking}
                          </Typography>
                        )}
                        {p.observations && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                            📝 {p.observations}
                          </Typography>
                        )}
                        {evidencePhotos.length > 0 && (
                          <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
                            {evidencePhotos.map((src, i) => (
                              <Avatar
                                key={i}
                                variant="rounded"
                                src={src}
                                sx={{ width: 22, height: 22, cursor: "pointer", border: `1px solid ${COLORS.line}` }}
                                onClick={() => openLightbox(src, t("deliverDialog.evidencePhotos"))}
                              />
                            ))}
                          </Stack>
                        )}
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" sx={{ display: "block" }}>
                      {t("historial.received")}<strong>{p.deliveredTo}</strong>
                      {p.deliveredToType === "otro" && t("historial.other")}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                      {t("historial.delivered")}{p.deliveredBy || "—"}
                    </Typography>
                    {p.signature && (
                      <Box
                        component="img"
                        src={p.signature}
                        onClick={() => openLightbox(p.signature, `${t("historial.received")}${p.deliveredTo}`)}
                        sx={{ height: 22, mt: 0.5, bgcolor: "#fff", border: `1px solid ${COLORS.line}`, borderRadius: 0.5, cursor: "pointer" }}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {fmtDate(p.deliveredAt)}
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {delivered.length > PAGE_SIZE && (
        <Stack alignItems="center" sx={{ mt: 2 }}>
          <Pagination count={totalPages} page={page} onChange={(_, v) => setPage(v)} size="small" />
        </Stack>
      )}

      <Dialog open={!!lightbox} onClose={() => setLightbox(null)} maxWidth="sm">
        {lightbox && (
          <Box sx={{ p: 2, bgcolor: COLORS.ink }}>
            <Typography variant="caption" sx={{ color: "#EDEFE6", mb: 1, display: "block" }}>
              {lightbox.label}
            </Typography>
            <Box component="img" src={lightbox.src} sx={{ maxWidth: "100%", maxHeight: "70vh", borderRadius: 1, bgcolor: "#fff" }} />
          </Box>
        )}
      </Dialog>
    </Box>
  );
}
